import * as React from "react"
import {Card, CardContent,} from "@/components/ui/card"
import {Input} from "@/components/ui/input"
import {Label} from "@/components/ui/label"
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue,} from "@/components/ui/select"
import {LLMOperator,} from "@/model/Workflow";
import {GPTPrompt, GPTPromptRoleType} from "@/model/llmSpecification";
import {Textarea} from "@/components/ui/textarea";
import {CirclePlus} from "lucide-react";
import {OperatorDetailProps} from "@/view/workflow/components/operatorForm/index";
import ReactJson from "react-json-view";
import useLlmModels from "@/query/workflow/useLlmModels";
import {useState} from "react";

interface LLMOperatorFormProps extends OperatorDetailProps {
    operator: LLMOperator
}


export const LLMOperatorForm: React.FC<LLMOperatorFormProps> = ({operator, readOnly = false}) => {
    const {data: llmVendorModels} = useLlmModels();
    const [vendor, setVendor] = useState<string>(operator.model.vendor);
    const [model, setModel] = useState<string>(operator.model.model);

    const onChangeVendor = (vendor: string) => {
        setVendor(vendor);
        operator.model.vendor = vendor;
        
        const firstModel = llmVendorModels?.[vendor]?.[0];
        if (firstModel) {
            setModel(firstModel);
            operator.model.model = firstModel;
        }
    }

    const onChangeModel = (model: string) => {
        setModel(model);
        operator.model.model = model;
    }

    return (
        <div className="grid w-full items-center gap-4 h-full">
            <Card className='p-6'>
                <CardContent className="grid gap-4">
                    <div className="flex flex-col space-y-1.5">
                        <Label htmlFor="name">Name</Label>
                        <Input id="name" placeholder="Name of your project" defaultValue={operator.name} readOnly={readOnly}/>
                    </div>
                    <div className="flex flex-col space-y-1.5">
                        <Label htmlFor="framework">LLM Providers</Label>
                        <Select value={vendor} onValueChange={onChangeVendor} disabled={readOnly}>
                            <SelectTrigger id="framework">
                                <SelectValue placeholder="Select"/>
                            </SelectTrigger>
                            <SelectContent position="popper">
                                {llmVendorModels && Object.keys(llmVendorModels).map((vendorItem) => (
                                    <SelectItem key={vendorItem} value={vendorItem}>{vendorItem}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex flex-col space-y-1.5">
                        <Label htmlFor="model">LLM Model</Label>
                        <Select value={model} onValueChange={onChangeModel} disabled={readOnly}>
                            <SelectTrigger id="model">
                                <SelectValue placeholder="Select"/>
                            </SelectTrigger>
                            <SelectContent position="popper">
                                {llmVendorModels && llmVendorModels[vendor]?.map((modelItem) => (
                                    <SelectItem key={modelItem} value={modelItem}>{modelItem}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <div className="flex flex-col space-y-1.5">
                <Label htmlFor="framework">Prompts</Label>

                <div className='flex flex-col gap-2 mt-3'>
                    {operator.prompts.map((prompt, index) => (
                        <PromptForm key={`prompt_${index}`} prompt={prompt} readonly={readOnly}/>
                    ))}
                    {!readOnly &&
                        <div className='flex justify-center'>
                            <CirclePlus role={'button'}/>
                        </div>
                    }
                </div>
            </div>

            <div className="flex flex-col space-y-1.5">
                <Label htmlFor="framework">Function Format</Label>
                <div className="flex flex-col gap-2">
                    <FunctionFormatForm functionFormat={operator.functionFormat}
                                        truncatedOnTokenLimit={operator.truncatedOnTokenLimit}
                                        readonly={true}/>
                </div>
            </div>
        </div>
    )
}

const PromptForm: React.FC<{ prompt: GPTPrompt, readonly: boolean}> = ({prompt, readonly}) => {
    const onChangeContent = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        prompt.content = e.target.value
    }

    const onChangeRole = (role: GPTPromptRoleType) => {
        prompt.role = role
    }
    return (
        <Card className='p-6'>
            <CardContent>
                <form>
                    <div className="grid w-full items-center gap-4">
                        <div className="flex flex-col space-y-1.5">
                            <Label htmlFor="framework">Role</Label>
                            <Select defaultValue={prompt.role} onValueChange={onChangeRole} disabled={readonly}>
                                <SelectTrigger id="framework">
                                    <SelectValue placeholder="Select"/>
                                </SelectTrigger>
                                <SelectContent position="popper">
                                    {Object.values(GPTPromptRoleType).map((type) => (
                                        <SelectItem key={type} value={type}>{type}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col space-y-1.5">
                            <Label htmlFor="name">Name</Label>
                            <Textarea placeholder="Type your message here."
                                      rows={15}
                                      disabled={readonly}
                                      defaultValue={prompt.content}
                                      onChange={onChangeContent}/>
                        </div>
                    </div>
                </form>
            </CardContent>

        </Card>
    )
}

const FunctionFormatForm: React.FC<{ functionFormat: Object, truncatedOnTokenLimit: Object[], readonly: boolean }> = (
    {functionFormat, truncatedOnTokenLimit,  readonly}
) => {
    return (
        <Card className='p-6'>
            <CardContent>
                <div className="grid w-full items-center gap-4">
                    <div className="flex flex-col space-y-1.5">
                        <Label htmlFor="framework">Format</Label>
                        <div className="flex min-h-[50px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                            <ReactJson src={functionFormat}
                                       name={false}
                                       collapsed={true}
                                       enableClipboard={true}
                                       displayDataTypes={false}
                                       displayObjectSize={true}
                            />
                        </div>

                    </div>
                    <div className="flex flex-col space-y-1.5">
                        <Label htmlFor="framework">TruncatedOnTokenLimit</Label>
                        <div className="flex min-h-[50px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                            <ReactJson src={truncatedOnTokenLimit}
                                       name={false}
                                       collapsed={true}
                                       enableClipboard={true}
                                       displayDataTypes={false}
                                       displayObjectSize={true}
                            />
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

export default LLMOperatorForm
